import { isEqual } from "lodash";
// Styles mapbox's own controls and popups, bundled from the package so it
// cannot drift from the mapbox-gl version the app is built against.
import "mapbox-gl/dist/mapbox-gl.css";
import * as mapboxgl from "mapbox-gl/esm";

import { Env } from "@/shared/environment";
import { LoggerService } from "@/shared/services/LoggerService";
import { MapPath } from "@/shared/state/visualization/schema/v1";
import {
  NS_PER_S,
  nanoSecToLocalTimestamp,
  nanoSecToSecLossy,
} from "@/shared/time";

import { ListenerType, Timer } from "../../timer/Timer";

import { defaultActiveMarker, defaultMarker } from "./colors";
import {
  CAMERA_MAX_ZOOM,
  GeoBounds,
  SOURCE_MAX_ZOOM,
  cameraFitForPathBounds,
  cameraFitForVisiblePaths,
} from "./geoBounds";
import { TimestampedLocation } from "./geoTypes";
import { MapDataManager } from "./MapDataManager";
import { MapPathData, representedSampleRange } from "./MapPathData";
import {
  type MapLoadingState,
  type MapPathDataSource,
} from "./MapPathDataSource";
import { ScaleBarControl } from "./ScaleBarControl";
import { StyleToggleControl } from "./StyleToggleControl";

/**
 * Ceiling on the clickable, hoverable sample markers one path draws, however
 * long the recording is.
 *
 * One marker per sample means a 20-minute recording at 100Hz draws 120,000 of
 * them, and a handful of such paths runs a browser tab out of memory. Those
 * samples land on top of each other on screen anyway, and the line underneath
 * already shows the route, so what the markers convey is where the samples
 * are, not how many there are. Bounding them makes the drawing cost the same
 * whatever the recording's length, and the selection is re-taken for each
 * viewport, so zooming in still resolves every sample.
 *
 * The budget is per path: a panel showing several paths draws up to this many
 * for each of them, and nothing bounds the panel-wide total.
 *
 * Where the number comes from: spread over a panel about 800 by 600 pixels,
 * 5000 markers sit roughly ten pixels apart and each is six pixels across,
 * about as dense as they can be drawn and still read as separate dots, which
 * is the whole point of drawing them. Nothing else depends on the value, so it
 * can move.
 */
export const MAX_RENDERED_POINTS_PER_PATH = 5000;

/**
 * How far beyond the viewport to select points, as a fraction of its span, so
 * that a small pan does not drag a visible edge of missing points behind it.
 */
const VIEWPORT_PADDING_FRACTION = 0.25;

/**
 * How far the drawn points may lag behind the data arriving for a
 * still-loading path.
 *
 * Every refresh rebuilds the selection and makes mapbox re-index the points
 * source, so mid-load, refreshes driven by data arrival are limited to one per
 * this interval. Camera moves are exempt, because the user is looking at the
 * result: a pan or zoom of their own, or a camera fit the renderer issued
 * settling, re-takes the selection immediately. A path that has finished
 * loading is exempt entirely.
 *
 * Where the number comes from: it is a staleness budget, not a cost
 * measurement. Half a second holds the drawn points close enough to the line
 * that the two do not read as disagreeing, while cutting a hundred refreshes
 * during a long load to a couple per second. It bounds something different
 * from MAX_REDRAW_DELAY_MS, which bounds how late a sample may reach the line,
 * and nothing goes wrong when the two disagree.
 */
export const POINTS_REFRESH_INTERVAL_MS = 500;

/**
 * How much a path must grow before it is redrawn, as a fraction of what is
 * already drawn.
 *
 * A redraw hands the source the whole line, so it costs every sample the path
 * has rather than the ones that prompted it: the payload is serialized to JSON
 * on the main thread, posted to the source's worker, parsed there, and every
 * tile the path occupies is rebuilt and uploaded to the GPU. Redrawing once
 * per chunk would pay that at every length the path passed through on the way
 * to its final one, so the total would grow with the square of the path.
 * Redrawing on proportional growth makes the number of redraws logarithmic in
 * the path's length, which brings the total back in step with the path.
 *
 * Where the number comes from: growing by a quarter each time, a path reaching
 * 120,000 samples from a first chunk of 100 takes log(1200) / log(1.25), about
 * 32 redraws, where one per chunk would be 1200. An eighth would make it 60
 * and a half would make it 18, so the returns either side are shallow. What
 * the fraction costs is the other end of the same trade: the samples not yet
 * drawn can be up to a quarter of the path behind, until MAX_REDRAW_DELAY_MS
 * or the end of the load releases them.
 */
const REDRAW_GROWTH_FRACTION = 0.25;

/**
 * The longest freshly arrived samples may wait before the line draws them, so
 * that a path which stops growing short of REDRAW_GROWTH_FRACTION, in the last
 * chunks of a load or during a stalled fetch, still reaches the screen.
 *
 * It does not apply to a path held back for a payload of its own still
 * outstanding, described in #flushPaths: that wait ends when the source
 * announces the payload loaded, or reports that it failed.
 *
 * Where the number comes from: it is the longest a user waits to see samples
 * that have already arrived, so it is bounded above by the point at which a
 * wait stops reading as part of the action that caused it, about a second.
 * Below that, the only pressure is to keep clear of the case where nothing has
 * gone wrong: a path still growing by a quarter at a time never reaches the
 * deadline, and a load that finishes releases immediately, so this only
 * governs a path that stalls.
 */
export const MAX_REDRAW_DELAY_MS = 750;

/**
 * The most samples one line feature spans.
 *
 * mapbox draws a line by expanding each coordinate into triangle vertices in
 * a buffer whose indices are 16-bit, so no more than 65,535 vertices can
 * share one buffer segment. It sizes a segment for a feature up front from
 * that feature's coordinate count, at its worst case of ten vertices per
 * coordinate, and starts a fresh segment when the estimate does not fit.
 * Nothing splits a feature that overruns the limit part way through, though,
 * so a single feature whose real vertex count passes 65,535 would wrap its
 * own indices and draw garbage.
 *
 * Splitting the line into features the estimate itself cannot exceed keeps
 * every feature inside one segment. mapbox clips features to tiles before it
 * counts, so what matters is how much of one feature lands in a single tile:
 * a path drawn zoomed out enough to sit inside one tile puts all of it there,
 * which is how a long path reaches this at all.
 */
export const MAX_SAMPLES_PER_LINE_FEATURE = Math.floor(
  // mapbox's own two numbers: the 16-bit index ceiling on one segment's
  // vertices, and the vertices per coordinate its line bucket sizes with.
  65535 / 10,
);

/**
 * Distinguishes a path's points source id from the line source id, which is
 * the path id itself.
 */
const POINTS_SOURCE_SUFFIX = "-points";

/*
 * Ids of the sources and layers the renderer lays down for one path. Exported
 * so a test observing a real map addresses them through the same functions the
 * renderer builds them with.
 */

/** Id of the source a path's line is drawn from: the path id itself. */
export function getLineSourceId(pathId: string): string {
  return pathId;
}

/** Id of the source carrying a path's drawn sample points. */
export function getPointsSourceId(pathId: string): string {
  return `${pathId}${POINTS_SOURCE_SUFFIX}`;
}

/** Id of the layer drawing a path's line. */
export function getLineLayerId(pathId: string): string {
  return `${pathId}-line`;
}

/** Id of the layer drawing a path's sample points as circles. */
export function getCircleLayerId(pathId: string): string {
  return `${pathId}-circle`;
}

/**
 * The source a failure came from, or undefined for a failure the map raised
 * about itself. Mapbox types the payload as carrying only the error and
 * attaches the source id alongside it at runtime.
 */
function erroringSourceId(event: mapboxgl.ErrorEvent): string | undefined {
  return (event as unknown as { sourceId?: string }).sourceId;
}

/**
 * Id of the source and of the layer every path's active-location marker
 * shares. Mapbox spends render time on each source and each layer in the
 * style, and these markers hold a single point apiece and all look alike, so
 * they are drawn together rather than a pair of objects per path.
 */
export const ACTIVE_LOCATIONS_ID = "active-locations";

const activeLayerStyle: NonNullable<
  mapboxgl.CircleLayerSpecification["paint"]
> = {
  "circle-radius": 6,
  "circle-color": defaultActiveMarker,
};

/**
 * How far past a tile's edge mapbox duplicates a source's features, so that a
 * shape straddling the edge is drawn whole by both tiles. The unit is 512ths
 * of a tile, which is a screen pixel when a tile is drawn at its own size and
 * more than that when the map is zoomed in past the tile's own zoom.
 *
 * The widest circle these sources draw has a radius of 6, so it needs about
 * seven pixels of reach to clear an edge along with its antialiasing. This is
 * twice that, which leaves room for a tile drawn smaller than its own size,
 * as every tile is once the camera is pitched and the ground recedes. Even so
 * it duplicates a point into a band a thirty-second of a tile wide on each of
 * the four sides, against the quarter of a tile mapbox's own default of 128
 * asks for: a tile carries about a tenth more features than it has to rather
 * than more than twice as many.
 */
const CIRCLE_TILE_BUFFER = 16;

/**
 * Window-level events mapbox forwards into Map.resize(): browser window
 * resize, device rotation, and entering or leaving fullscreen. Map.resize()
 * fires movestart carrying that event as originalEvent even though the camera
 * has not moved, so these must not count as the user taking the camera over.
 */
const resizeEventTypes = new Set([
  "resize",
  "orientationchange",
  "fullscreenchange",
  "webkitfullscreenchange",
]);

/** A handler mapbox calls with the pointer's position over a named layer. */
type HoverListener = (event: mapboxgl.MapMouseEvent) => void;

/** What a host learns when a click lands on one of a path's drawn points. */
export interface PathClick {
  pathId: string;
}

/**
 * Whether the new path list differs from the previous one only in how its
 * paths are styled: the same path ids, each still shown or hidden as
 * before and with the same coordinate units. A recolor or a change of
 * opacity is style-only; a path added, removed, hidden, shown, or reloaded
 * for new units is not.
 */
function isStyleOnlyChange(
  previous: MapPath[] | undefined,
  next: MapPath[],
): boolean {
  if (previous === undefined || previous.length !== next.length) {
    return false;
  }
  const previousById = new Map(previous.map((path) => [path.id, path]));
  return next.every((path) => {
    const existing = previousById.get(path.id);
    return (
      existing !== undefined &&
      existing.visible === path.visible &&
      isEqual(existing.coordinateUnits, path.coordinateUnits)
    );
  });
}

/**
 * Everything the renderer tracks about one path it has laid onto the map.
 *
 * All of a path's render bookkeeping lives in this one record with one
 * lifecycle: it is created when the path's sources are added and deleted when
 * they are torn down, so no facet of it can outlive or predate the others.
 */
interface PathDrawing {
  /**
   * Number of samples the line on the map covers: the source's worker
   * reported the payload carrying them loaded. Growth is measured against
   * this rather than against the payload most recently sent, so a redraw is
   * judged by how far the drawn line has fallen behind the data.
   */
  lineSamplesConfirmed: number;
  /**
   * Number of samples covered by a line payload the source accepted but has
   * not yet confirmed, or null when nothing is outstanding. Promoted into
   * lineSamplesConfirmed when the source announces the load succeeded, and
   * cleared without promotion when it fails. Starts at zero rather than null,
   * because a source's creation counts as a payload of no samples, and the
   * path's first redraw waits behind it like any other.
   */
  lineSamplesSent: number | null;
  /**
   * When the samples beyond the last line payload must be drawn by, whatever
   * they have grown to, or null when no samples are waiting to be sent. A
   * non-null deadline is what marks the path as waiting for a flush.
   */
  flushDeadline: number | null;
  /**
   * The sample indices the path's drawn points stand for, ascending. A click
   * on a drawn point is resolved against the undrawn samples between its
   * neighbours in here, which is what lets a click seek to the true nearest
   * sample rather than snap to the one drawn.
   */
  pointSelection: number[];
  /**
   * When the point selection was last re-taken, against which
   * POINTS_REFRESH_INTERVAL_MS is measured. Null until the first refresh
   * after the sources were laid down.
   */
  pointsRefreshedAt: number | null;
  /**
   * The popup handlers watching the path's circle layer, held because mapbox
   * only takes a listener off again by identity. Null between the record
   * being created and the layers it watches being added.
   */
  hoverListeners: {
    mousemove: HoverListener;
    mouseleave: HoverListener;
  } | null;
}

/**
 * Draws a Map panel's paths onto a mapbox GL map: the line each path traces, a
 * bounded selection of its samples as clickable circles, and one marker per
 * visible path at the playback time the panel's timer is at.
 *
 * MapPanel builds one per mounted panel and holds it for the panel's life.
 * `initialize()` has to resolve before any state is handed over, and
 * `dispose()` has to run when the panel unmounts, or the map keeps its WebGL
 * context and its listeners on the timer. After `dispose()` every public entry
 * point returns without doing anything.
 */
export class MapRenderer {
  #abortController: AbortController;
  #map: mapboxgl.Map | null = null;
  #dataSource: MapPathDataSource;
  #timer?: Timer;
  #state?: MapPath[];
  #mapContainer: HTMLDivElement;
  #shouldFitCameraToPathBounds: boolean;
  #setSingleActiveLocation: (arg: TimestampedLocation | null) => void;
  #dataLoadHold: symbol | null = null;
  /** Keyed by path id. */
  #drawings: Map<string, PathDrawing> = new Map();
  #flushFrame: number | null = null;
  /**
   * Fires when the earliest deadline among paths held back for growth
   * passes, so a path that stops growing is flushed on time without being
   * checked every frame. Null when nothing is held for growth.
   */
  #flushDeadlineTimer: ReturnType<typeof setTimeout> | null = null;
  /**
   * Set when the renderer itself asks the camera to move, cleared by the
   * moveend that ends the move. The point selections depend on the viewport,
   * so they are re-taken for where the camera settles rather than for the
   * viewport it was about to leave.
   */
  #refreshPointsWhenCameraSettles = false;
  /** Callback errors are reported through; also handed to the default data
   *  source. */
  #onError: (error: Error) => void;

  /**
   * Whether the camera still owes newly arrived data a re-fit, and the corners
   * it was last actually fitted to.
   *
   * `fitBounds` re-renders the viewport, which over a load costs more than
   * building the GeoJSON does, and a path's extent stops changing long before
   * its samples stop arriving. Coalescing the request onto a frame and skipping
   * a fit that would not move the camera turns hundreds of re-renders into a
   * handful.
   */
  #cameraFitPending = false;
  #lastFittedCorners: [[number, number], [number, number]] | null = null;
  #styleLoadHold: symbol | null = null;
  #popup: mapboxgl.Popup | null = null;
  #onViewportChange?: (viewport: {
    center: { lng: number; lat: number };
    zoom: number;
    bearing: number;
    pitch: number;
  }) => void;
  #initialViewport?: {
    center: { lng: number; lat: number };
    zoom: number;
    bearing?: number;
    pitch?: number;
  };
  #refitCameraOnStyleChange: boolean;
  #onPathClick?: (click: PathClick) => void;

  constructor({
    abortController,
    mapContainer,
    onError,
    setLoadingState,
    timer,
    setSingleActiveLocation,
    onViewportChange,
    initialViewport,
    dataSource,
    refitCameraOnStyleChange = true,
    onPathClick,
  }: {
    abortController: AbortController;
    mapContainer: HTMLDivElement;
    onError: (error: Error) => void;
    setLoadingState: (state: MapLoadingState) => void;
    /**
     * The workspace's playback timer, which places the active-location
     * markers and which a click on a drawn point seeks. A host without
     * playback (a board's map panel) omits it, which turns both off; paths,
     * hover popups, and `onPathClick` work without one.
     */
    timer?: Timer;
    setSingleActiveLocation: (arg: TimestampedLocation | null) => void;
    onViewportChange?: (viewport: {
      center: { lng: number; lat: number };
      zoom: number;
      bearing: number;
      pitch: number;
    }) => void;
    initialViewport?: {
      center: { lng: number; lat: number };
      zoom: number;
      bearing?: number;
      pitch?: number;
    };
    /**
     * Where the paths' coordinates come from. Defaults to the visualizer's
     * `MapDataManager`, which reads each path's bound topic data in a
     * per-panel worker. A host that reads coordinates another way (a board's
     * map panel reads the rows of its bound stages) passes its own
     * implementation.
     */
    dataSource?: MapPathDataSource;
    /**
     * Whether a `setState` that changes only how the paths are styled (a
     * recolor, a change of opacity) re-fits the camera to the visible paths
     * as every other `setState` does. Defaults to true. A host that restyles
     * paths while the user reads the map (a board's map panel, which lowers
     * the opacity of every path no highlight-mode selection names) passes
     * `false` so the camera stays where it is. A path added, removed,
     * hidden, or shown re-fits either way.
     */
    refitCameraOnStyleChange?: boolean;
    /**
     * Called when a click lands on one of a path's drawn points, with the
     * path's id. A host without a `timer` (a board's map panel) learns
     * which path was clicked from this call; the seek through `timer`, when
     * there is one, happens as well.
     */
    onPathClick?: (click: PathClick) => void;
  }) {
    this.#abortController = abortController;
    this.#mapContainer = mapContainer;
    this.#onError = onError;
    this.#timer = timer;
    this.#setSingleActiveLocation = setSingleActiveLocation;
    this.#onViewportChange = onViewportChange;
    this.#initialViewport = initialViewport;
    this.#refitCameraOnStyleChange = refitCameraOnStyleChange;
    this.#onPathClick = onPathClick;
    // A viewport restored from a shared workspace or a saved layout is the
    // user's own framing, so it wins over fitting the camera to path bounds.
    this.#shouldFitCameraToPathBounds = initialViewport === undefined;

    this.#dataSource = dataSource ?? new MapDataManager({ onError });
    this.#dataSource.setOnPathUpdated(this.#onPathDataUpdated);
    this.#dataSource.setOnPathComplete(this.#onPathDataComplete);
    this.#dataSource.setOnLoadingStateChange((state) => {
      setLoadingState(state);

      if (state !== "complete") {
        this.#dataLoadHold ??= this.#timer?.hold() ?? null;
      } else {
        if (this.#dataLoadHold) {
          this.#timer?.holdRelease(this.#dataLoadHold);
          this.#dataLoadHold = null;
        }
        // Playback is usually paused while data loads, so no timer tick is
        // coming to put the newly loaded paths' markers on the map.
        this.drawActiveLocation();
      }
    });
  }

  /**
   * The mapbox map this renderer drives. Null before `initialize()` is called
   * and again after `dispose()`; in between it is set, though the style has
   * not necessarily loaded until `initialize()` resolves.
   */
  public get map(): mapboxgl.Map | null {
    return this.#map;
  }

  /**
   * Resolves once the map's first style has loaded and the data source is
   * ready, which is the point at which paths can be drawn. MapPanel awaits this
   * before handing over any state, and shows a rejection as the panel's error.
   */
  public initialize(): Promise<[void, boolean]> {
    return Promise.all([
      this.#initializeMap(),
      this.#dataSource.waitForInitialized(),
    ]);
  }

  #initializeMap(): Promise<void> {
    return new Promise((resolve, reject) => {
      const center: [number, number] = this.#initialViewport?.center
        ? [this.#initialViewport.center.lng, this.#initialViewport.center.lat]
        : [0, 0];
      const zoom = this.#initialViewport?.zoom ?? 19;
      const bearing = this.#initialViewport?.bearing ?? 0;
      const pitch = this.#initialViewport?.pitch ?? 0;

      this.#map = new mapboxgl.Map({
        // The ESM entry exposes the module namespace read-only, so the token
        // is handed to each map rather than assigned globally.
        accessToken: Env.robotoMapboxToken,
        container: this.#mapContainer,
        center,
        style: "mapbox://styles/mapbox/outdoors-v12", // https://docs.mapbox.com/api/maps/styles/
        zoom: zoom,
        maxZoom: CAMERA_MAX_ZOOM,
        bearing: bearing,
        pitch: pitch,
      });

      // Failures arrive as error events, not throws: a GeoJSON payload the
      // worker rejects, a tile request that came back with an error status, a
      // style that would not load. Unlistened, mapbox degrades them to a
      // console line and the map goes on showing stale data with no signal, so
      // they are routed to the same onError callback the data source reports
      // through. Before the style has loaded, one also means initialization
      // failed; rejecting an already-settled promise does nothing, so the same
      // handler covers both.
      this.#map.on("error", (evt) => {
        if (this.#isBasemapSourceError(evt)) {
          // Only `evt.error` is logged. The event's `target` is the map, and
          // a console that holds logged objects (an attached DevTools) would
          // keep the map, this renderer, and the panel's React tree alive
          // for as long as it keeps the message, after the panel unmounted.
          LoggerService.warn(
            `Map tiles failed to load (source "${erroringSourceId(evt)}"). ` +
              "The panel's own paths and markers are unaffected.",
            evt.error,
          );
          return;
        }
        reject(evt.error);
        this.#onMapError(evt);
      });

      // A GeoJSON source announces each finished payload load with this
      // event. It is what confirms a line payload landed, and what resumes a
      // path whose samples were held back while that payload was outstanding.
      this.#map.on("sourcedata", (evt) => {
        this.#onSourceData(evt);
      });

      // Once the user moves the camera themselves, they own it: stop fitting
      // to path bounds for the rest of the panel's life. Mapbox sets
      // originalEvent on movestart for camera gestures (drag, wheel,
      // keyboard, double-click, NavigationControl) and leaves it unset for
      // programmatic moves such as fitBounds, so its presence is the signal.
      this.#map.on("movestart", (e) => {
        if (e.originalEvent && !resizeEventTypes.has(e.originalEvent.type)) {
          this.#shouldFitCameraToPathBounds = false;
        }
      });
      // Box zoom (shift+drag) moves the camera through fitScreenCoordinates,
      // a programmatic call, so its movestart carries no originalEvent, but
      // boxzoomend does.
      this.#map.on("boxzoomend", () => {
        this.#shouldFitCameraToPathBounds = false;
      });
      // Scroll zoom is the other gesture movestart can miss. Its handler moves
      // the camera from a render frame and attaches the wheel event only once
      // the gesture has been classified, which the first tick of a gesture
      // often is not: in mapbox-gl 3.29.0, a mouse-wheel tick that
      // demonstrably moves the camera fires movestart with originalEvent
      // undefined, while dblclick and keydown both carry theirs. So watch the
      // gesture itself. A wheel over the map is the user reaching for the
      // camera whether or not mapbox acts on it, and Map.resize() cannot
      // manufacture one.
      this.#map.getCanvasContainer().addEventListener(
        "wheel",
        () => {
          this.#shouldFitCameraToPathBounds = false;
        },
        { signal: this.#abortController.signal },
      );

      // Report where the camera came to rest, so the panel can keep the user's
      // framing in the layout and restore it the next time the panel opens.
      this.#map.on("moveend", () => {
        if (!this.#map || !this.#onViewportChange) {
          return;
        }

        const center = this.#map.getCenter();
        const zoom = this.#map.getZoom();
        const bearing = this.#map.getBearing();
        const pitch = this.#map.getPitch();

        this.#onViewportChange({
          center: { lng: center.lng, lat: center.lat },
          zoom,
          bearing,
          pitch,
        });
      });

      this.#map.addControl(
        new mapboxgl.NavigationControl({
          showCompass: false,
        }),
        "top-right",
      );

      this.#map.addControl(new mapboxgl.FullscreenControl(), "top-right");

      // The fit runs to CAMERA_MAX_ZOOM for any path with extent, so a track
      // metres across and one centimetres across are framed identically and
      // the basemap is overscaled past legibility in both. The scale bar is
      // the only thing left that says which one is on screen. Mapbox's own
      // ScaleControl cannot measure ground this small; see scaleBar.ts.
      this.#map.addControl(new ScaleBarControl());

      this.#map.addControl(
        new StyleToggleControl({
          onStyleLoad: () => {
            this.redrawAfterStyleChange();
          },
        }),
        "top-right",
      );

      // Panning or zooming changes which samples are on screen, and how many
      // of them fit in the budget. Zooming in far enough drops the stride to
      // one, which is what puts the full-resolution path back on the map.
      this.#map.on("moveend", (event) => {
        // A camera fit the renderer issued has now settled, so this is the
        // viewport its point selections belong to.
        const cameraFitSettled = this.#refreshPointsWhenCameraSettles;
        this.#refreshPointsWhenCameraSettles = false;

        // mapbox sets originalEvent on the moveend of most camera gestures
        // and leaves it unset for a programmatic move such as fitBounds, so a
        // missing originalEvent is read as renderer-initiated. Two gestures
        // also arrive without one: box zoom moves the camera through
        // fitScreenCoordinates, and a coarse trackpad scroll reaches the
        // camera from a render frame before its wheel event is classified.
        // Misreading those two costs at most the mid-load refresh throttle; a
        // finished path answers every move immediately either way.
        this.#refreshAllPathPoints(
          event.originalEvent !== undefined || cameraFitSettled,
        );
      });

      this.#map.on("click", this.onMapClick.bind(this));

      const timerEvents: ListenerType[] = ["tick", "seek", "start", "stop"];
      timerEvents.forEach((event) =>
        this.#timer?.addListener(event, this.drawActiveLocation.bind(this), {
          signal: this.#abortController.signal,
        }),
      );
      this.#map.on("styledataloading", () => {
        if (this.#styleLoadHold !== null || this.#timer === undefined) {
          return;
        }
        this.#styleLoadHold = this.#timer.hold();
      });
      this.#map.on("styledata", () => {
        if (this.#styleLoadHold === null || this.#timer === undefined) {
          return;
        }
        this.#timer.holdRelease(this.#styleLoadHold);
        this.#styleLoadHold = null;
      });

      this.#popup = new mapboxgl.Popup({
        closeButton: false,
        closeOnClick: false,
        maxWidth: "300px",
        offset: 10,
        anchor: "top",
      });

      this.#map.once("style.load", () => resolve());
    });
  }

  /**
   * Fit the camera to one path's full extent. Invoked when the user picks a
   * path in the legend; that pick is the user choosing the framing, so later
   * data chunks stop re-fitting the camera to every visible path. Does
   * nothing until the path has coordinates.
   */
  public fitCameraToPath(pathId: string): void {
    if (!this.#map) {
      return;
    }

    const cameraFit = cameraFitForPathBounds(
      this.#dataSource.getPathData(pathId)?.getBounds(),
    );
    if (!cameraFit) {
      return;
    }

    this.#shouldFitCameraToPathBounds = false;
    this.#refreshPointsWhenCameraSettles = true;
    this.#map.fitBounds(cameraFit.corners, cameraFit.options);
  }

  /**
   * Tear the panel's map down when it unmounts: abort the loads in flight,
   * drop the work waiting on a frame or a deadline timer, and release the
   * map's WebGL context and the popup. The renderer is not reusable
   * afterwards (see the class docstring).
   */
  public dispose() {
    this.#abortController.abort();

    if (this.#flushFrame !== null) {
      cancelAnimationFrame(this.#flushFrame);
      this.#flushFrame = null;
    }
    if (this.#flushDeadlineTimer !== null) {
      clearTimeout(this.#flushDeadlineTimer);
      this.#flushDeadlineTimer = null;
    }
    this.#drawings.clear();
    this.#cameraFitPending = false;
    this.#refreshPointsWhenCameraSettles = false;

    this.#dataSource.dispose();
    this.#map?.remove();
    this.#popup?.remove();

    this.#map = null;
    this.#popup = null;
    this.#timer = undefined;
    this.#state = undefined;
  }

  /**
   * Lay every path down again on a map whose tile style has just changed.
   *
   * Mapbox destroys every source and layer when a style loads, so nothing the
   * renderer put on the map survives the switch, and the drawings it kept
   * describe sources that are gone. Redrawing here is the only thing that
   * brings the paths back: a path whose data has finished streaming has no
   * later chunk to arrive and trigger a draw of its own.
   */
  public redrawAfterStyleChange(): void {
    for (const path of this.#state ?? []) {
      const pathData = this.#dataSource.getPathData(path.id);
      if (pathData && pathData.length > 0) {
        this.drawPathData(path.id);
      }
    }
    this.drawActiveLocation();
  }

  /**
   * Tell mapbox its container changed size, so the canvas matches it again.
   * MapPanel calls this from a debounced observer on the container.
   */
  public resize(): void {
    if (!this.#map) {
      return;
    }

    this.#map.resize();
    // The same bounds frame differently in a differently shaped container, so
    // the last fit is no longer the answer to compare against.
    this.#lastFittedCorners = null;
  }

  /**
   * Bring the map in line with the panel's path list. Runs whenever that list
   * changes: a path added or deleted, hidden or shown, recolored, or its
   * coordinate units changed.
   *
   * The three reconciling passes below each work out what to do by comparing
   * against the previous list, so `#state` is only replaced once all three
   * have run.
   *
   * A change that only restyles paths leaves every path's extent and
   * visibility as it was, so the camera has nothing new to frame; for a
   * host that passed `refitCameraOnStyleChange: false`, the camera stays
   * where it is through such a change.
   */
  public setState(state: MapPath[]): void {
    if (!this.#map) {
      return;
    }

    const holdCamera =
      !this.#refitCameraOnStyleChange && isStyleOnlyChange(this.#state, state);

    this.removeOldPaths(state);
    this.reloadPathsWithChangedUnits(state);
    this.addNewPaths(state);

    const previouslyHidden = new Set(
      (this.#state ?? [])
        .filter((path) => !path.visible)
        .map((path) => path.id),
    );
    this.#state = state;
    // Paths may have been added, removed, or toggled hidden, so the last fit
    // is no longer the answer to compare against.
    if (!holdCamera) {
      this.#lastFittedCorners = null;
    }

    for (const path of state) {
      if (this.#map.getSource(getLineSourceId(path.id))) {
        this.applyPathStyle(path);
        // A hidden path sits out the per-camera-move refreshes, so the
        // selection it kept may be stale for the current viewport and has to
        // be re-taken the moment the path is shown again.
        if (path.visible && previouslyHidden.has(path.id)) {
          this.#refreshPathPoints(path.id, true);
        }
      }
    }

    // Playback is often paused when a path is hidden or removed, so there is
    // no timer event coming to take the departed path's marker off the map.
    this.drawActiveLocation();

    // For a path that finished loading before this change, there is no later
    // data chunk coming to trigger a fit, so re-frame here.
    if (!holdCamera) {
      this.fitCameraToVisiblePathBounds();
    }
  }

  /**
   * Start loading data for paths the panel did not have before. A path already
   * in the previous state is left alone, whether or not its load has finished.
   */
  private addNewPaths(state: MapPath[]): void {
    for (const path of state) {
      if (this.#state?.some((existing) => existing.id === path.id)) {
        continue;
      }

      if (!this.#dataSource.getPathData(path.id)) {
        this.#dataSource.loadPathData(path);
      }
    }
  }

  /**
   * Refetch a path whose coordinate units the user changed in panel settings.
   * The units decide how every sample is interpreted, so the loaded data and
   * everything drawn from it are thrown away rather than reconciled.
   */
  private reloadPathsWithChangedUnits(state: MapPath[]): void {
    for (const newPath of state) {
      const existingPath = this.#state?.find((p) => p.id === newPath.id);
      if (!existingPath) {
        continue;
      }

      if (isEqual(existingPath.coordinateUnits, newPath.coordinateUnits)) {
        continue;
      }

      this.#teardownPath(newPath.id);
      this.#dataSource.disposePath(existingPath);
      this.#dataSource.loadPathData(newPath);
    }
  }

  /**
   * Remove every map object and cached render state belonging to a path,
   * leaving nothing behind that a later path of the same id would inherit.
   *
   * Layers go before the sources they draw from, and each removal is guarded:
   * a path dropped before its first chunk arrived never got sources, and
   * mapbox throws on removing one that is not there. The path's
   * active-location marker is not here, because it is a feature of a source
   * every path shares; drawActiveLocation rebuilds that source from the paths
   * that are left. Does not dispose the path's data; that belongs to the data
   * source, and callers do it.
   */
  #teardownPath(pathId: string): void {
    for (const layerId of [getCircleLayerId(pathId), getLineLayerId(pathId)]) {
      if (this.#map?.getLayer(layerId)) {
        this.#map.removeLayer(layerId);
      }
    }

    for (const sourceId of [
      getLineSourceId(pathId),
      getPointsSourceId(pathId),
    ]) {
      if (this.#map?.getSource(sourceId)) {
        this.#map.removeSource(sourceId);
      }
    }

    this.#removeHoverInteraction(pathId);
    this.#drawings.delete(pathId);
  }

  /**
   * Add a source and the layers drawing from it, skipping whatever the style
   * already holds. Mapbox rejects a repeated source or layer id, and callers
   * here run again on paths that are already on the map.
   *
   * Every source created here is indexed to SOURCE_MAX_ZOOM. Stamping it in
   * one place is what keeps a later source from silently taking mapbox's
   * default of 18, whose grid cell is about 1.5cm on the ground -- enough to
   * round a centimetre-scale track down to a single point. A caller may still
   * pass its own `maxzoom` if it has reason to.
   */
  private addSourceAndLayers(
    sourceId: string,
    sourceData: mapboxgl.GeoJSONSourceSpecification,
    layers: mapboxgl.LayerSpecification[],
  ) {
    if (!this.#map) {
      return;
    }

    if (!this.#map?.getSource(sourceId)) {
      this.#map?.addSource(sourceId, {
        maxzoom: SOURCE_MAX_ZOOM,
        ...sourceData,
      });
    }

    for (const layer of layers) {
      if (!this.#map?.getLayer(layer.id)) {
        this.#map?.addLayer(layer);
      }
    }
  }

  /**
   * Build the marker collection: one point per path, naming the path it
   * belongs to and the time it sits at, which is what a click on one resolves
   * back to.
   */
  private createActiveLocationGeoJSON(
    locations: { location: TimestampedLocation; pathId: string }[],
  ): GeoJSON.FeatureCollection<GeoJSON.Geometry> {
    return {
      type: "FeatureCollection",
      features: locations.map(({ location, pathId }) => ({
        type: "Feature",
        properties: {
          id: pathId,
          timestamp: location.timestamp,
        },
        geometry: {
          type: "Point",
          coordinates: [location.longitude, location.latitude],
        },
      })),
    };
  }

  /**
   * Book the next frame to hand mapbox everything that has piled up: the
   * samples paths are waiting to flush, and the re-fit newly arrived data
   * has asked the camera for. Chunks landing before the frame runs are merged
   * into the same payload rather than each costing mapbox a re-index.
   */
  #scheduleFlush(): void {
    if (this.#flushFrame !== null) {
      return;
    }
    this.#flushFrame = requestAnimationFrame(() => {
      this.#flushFrame = null;

      // The camera goes first: when newly arrived data moves it, a point
      // selection taken during the flush would be for the viewport the
      // camera is about to leave, so the flush skips selections and the
      // fit's own moveend re-takes them for where the camera settles.
      let cameraMoving = false;
      if (this.#cameraFitPending) {
        this.#cameraFitPending = false;
        cameraMoving = this.fitCameraToVisiblePathBounds();
      }

      this.#flushPaths({ refreshPoints: !cameraMoving });
    });
  }

  /**
   * Redraw every waiting path that has grown enough, or waited long enough,
   * to be worth a write. Nothing here polls: a path with a payload still
   * outstanding is resumed by that source's data event, and one held back for
   * growth is resumed by the next chunk, by the load completing, or by the
   * flush deadline timer.
   */
  #flushPaths({ refreshPoints }: { refreshPoints: boolean }): void {
    if (!this.#map) {
      return;
    }

    let earliestHeldDeadline: number | null = null;
    for (const [pathId, drawing] of this.#drawings) {
      if (drawing.flushDeadline === null) {
        continue;
      }

      const source = this.#map.getSource(getLineSourceId(pathId));
      if (!source || source.type !== "geojson") {
        // A style switch destroyed the path's source; drawPathData lays it
        // down again in full, so there is nothing to redraw here.
        drawing.flushDeadline = null;
        continue;
      }

      if (drawing.lineSamplesSent !== null) {
        // A payload of this path's is still in the source's hands, and
        // mapbox does not queue a second one: it keeps only the most recent
        // data handed to a busy source, so each write made before the
        // outstanding load ends is replaced by the one after it, and the
        // collection built for it never reaches the worker. Writing would
        // also spoil the count above, because a source announces a finished
        // load without saying which payload it covered, so
        // lineSamplesConfirmed would advance to samples the worker has not
        // indexed. The source announcing the load finished, or reporting that
        // it failed, is what ends this wait; no deadline applies to it.
        continue;
      }

      const pathData = this.#dataSource.getPathData(pathId);
      const drawnCount = drawing.lineSamplesConfirmed;
      if (!pathData || pathData.length <= drawnCount) {
        drawing.flushDeadline = null;
        continue;
      }

      // Hold a small amount of growth back so it goes out with the next
      // chunk, unless the path has finished loading or has waited long
      // enough that the samples are overdue on screen.
      const newPoints = pathData.length - drawnCount;
      const overdue = performance.now() >= drawing.flushDeadline;
      if (
        drawnCount > 0 &&
        !pathData.isComplete &&
        !overdue &&
        newPoints < drawnCount * REDRAW_GROWTH_FRACTION
      ) {
        earliestHeldDeadline = Math.min(
          earliestHeldDeadline ?? Infinity,
          drawing.flushDeadline,
        );
        continue;
      }

      drawing.flushDeadline = null;
      source.setData(this.createLineGeoJSON(pathData));
      drawing.lineSamplesSent = pathData.length;
      // New samples change which points the selection lands on.
      if (refreshPoints) {
        this.#refreshPathPoints(pathId);
      }
    }

    this.#armFlushDeadlineTimer(earliestHeldDeadline);
  }

  /**
   * Keep one timer pointed at the earliest deadline among paths held back
   * for growth. Deadlines are assigned in arrival order, so a timer armed
   * earlier never fires later than the current earliest deadline; firing
   * early is harmless, because the flush it triggers re-arms for whatever is
   * still held.
   */
  #armFlushDeadlineTimer(earliestDeadline: number | null): void {
    if (earliestDeadline === null) {
      if (this.#flushDeadlineTimer !== null) {
        clearTimeout(this.#flushDeadlineTimer);
        this.#flushDeadlineTimer = null;
      }
      return;
    }

    if (this.#flushDeadlineTimer !== null) {
      return;
    }

    this.#flushDeadlineTimer = setTimeout(
      () => {
        this.#flushDeadlineTimer = null;
        this.#scheduleFlush();
      },
      Math.max(0, earliestDeadline - performance.now()),
    );
  }

  /**
   * The area to draw points for: what is on screen, widened a little so a
   * small pan does not immediately run past the selection.
   */
  #pointSelectionBounds(): GeoBounds | undefined {
    if (!this.#map) {
      return undefined;
    }

    const viewport = this.#map.getBounds();
    if (!viewport) {
      return undefined;
    }

    const minLat = viewport.getSouth();
    const maxLat = viewport.getNorth();
    const minLon = viewport.getWest();
    const maxLon = viewport.getEast();
    const latPadding = (maxLat - minLat) * VIEWPORT_PADDING_FRACTION;
    const lonPadding = (maxLon - minLon) * VIEWPORT_PADDING_FRACTION;

    return {
      minLat: minLat - latPadding,
      maxLat: maxLat + latPadding,
      minLon: minLon - lonPadding,
      maxLon: maxLon + lonPadding,
    };
  }

  /**
   * Points for one path, chosen for the current viewport.
   *
   * Each refresh re-takes the selection from scratch and hands the source all
   * of it, so points chosen for an earlier viewport stop being drawn instead
   * of accumulating behind the current ones.
   */
  #refreshPathPoints(pathId: string, force = false): void {
    if (!this.#map) {
      return;
    }

    const source = this.#map.getSource(getPointsSourceId(pathId));
    if (!source || source.type !== "geojson") {
      return;
    }

    const drawing = this.#drawings.get(pathId);
    const pathData = this.#dataSource.getPathData(pathId);
    if (!drawing || !pathData) {
      return;
    }

    // A loading path's selection is about to be invalidated again by the
    // next chunk, so it is not worth rebuilding on each one.
    const now = performance.now();
    const since = now - (drawing.pointsRefreshedAt ?? -Infinity);
    if (!force && !pathData.isComplete && since < POINTS_REFRESH_INTERVAL_MS) {
      return;
    }
    drawing.pointsRefreshedAt = now;

    const { collection, indices } = this.createPointsGeoJSON(pathData);
    drawing.pointSelection = indices;
    source.setData(collection);
  }

  /**
   * Re-take every visible path's point selection for the current viewport.
   * A hidden path is skipped: its layer draws nothing, so re-selecting and
   * re-indexing its points would be pure cost, and the selection is re-taken
   * when the path is toggled visible again.
   *
   * `force` bypasses the mid-load refresh throttle. It is set for a camera
   * move whose result the user is looking at: a gesture of their own, or a
   * camera fit the renderer issued settling. A path that has finished
   * loading is never throttled, so for it `force` changes nothing.
   */
  #refreshAllPathPoints(force: boolean): void {
    for (const path of this.#state ?? []) {
      if (!path.visible) {
        continue;
      }
      this.#refreshPathPoints(path.id, force);
    }
  }

  /**
   * A path's drawn points: at most MAX_RENDERED_POINTS_PER_PATH of those in
   * view, evenly spread. Returns the sample indices drawn alongside the
   * collection, so the caller can record what a click has to resolve against.
   */
  private createPointsGeoJSON(pathData: MapPathData): {
    collection: GeoJSON.FeatureCollection<GeoJSON.Geometry>;
    indices: number[];
  } {
    const indices = pathData.selectIndicesWithin(
      this.#pointSelectionBounds(),
      MAX_RENDERED_POINTS_PER_PATH,
    );

    const features: GeoJSON.Feature<GeoJSON.Geometry>[] = [];
    for (const index of indices) {
      const coord = pathData.getCoordinate(index);
      if (!coord) {
        continue;
      }

      features.push({
        type: "Feature",
        // A click on a drawn point recovers its sample index from
        // `properties.id`. The feature carries no top-level id because
        // nothing here calls setFeatureState, which is what mapbox reads a
        // feature id for.
        properties: {
          id: index,
          timestamp: nanoSecToSecLossy(coord.logTime),
        },
        geometry: {
          type: "Point",
          coordinates: [coord.lon, coord.lat],
        },
      });
    }

    return { collection: { type: "FeatureCollection", features }, indices };
  }

  /**
   * A path's whole line, as a collection to hand the path's line source. A
   * write replaces everything the source holds, so every collection carries
   * every sample streamed so far, not only those since the last write.
   *
   * A line spanning more than MAX_SAMPLES_PER_LINE_FEATURE samples becomes
   * several features rather than one, each picking up at the last sample of
   * the one before it so the drawn line stays unbroken. A path that holds
   * several lines (`MapPathData.lines`) draws each as features of its own,
   * with nothing joining one line's last sample to the next line's first.
   */
  private createLineGeoJSON(
    pathData: MapPathData,
  ): GeoJSON.FeatureCollection<GeoJSON.Geometry> {
    const features: GeoJSON.Feature<GeoJSON.Geometry>[] = [];

    // Each feature ends on the sample the next one starts at, so the step is
    // one short of the span. Stopping one before the end drops a trailing run
    // of a single sample, which the previous feature already ends on, and
    // keeps a path holding one sample from producing a one-coordinate
    // LineString, which GeoJSON does not allow.
    for (const line of pathData.lines) {
      for (
        let start = line.fromIndex;
        start < line.toIndexExclusive - 1;
        start += MAX_SAMPLES_PER_LINE_FEATURE - 1
      ) {
        features.push({
          type: "Feature",
          // Nothing reads a line feature's properties: the line layers are
          // not among the ones a click queries, and the layer paints a
          // constant color and width rather than an expression over the
          // feature.
          properties: null,
          geometry: {
            type: "LineString",
            coordinates: pathData.toGeoJSONCoordinates(
              start,
              Math.min(
                start + MAX_SAMPLES_PER_LINE_FEATURE,
                line.toIndexExclusive,
              ),
            ),
          },
        });
      }
    }

    return { type: "FeatureCollection", features };
  }

  /**
   * Put a path's samples on the map: lay its sources and layers down the first
   * time, and queue a redraw of its line every time after.
   *
   * Runs on each streamed chunk, when a path's load completes, and after a
   * tile-style switch has destroyed everything on the map. Does nothing until
   * the path has both coordinates and an entry in the panel's state.
   */
  private drawPathData(pathId: string): void {
    if (!this.#map) {
      return;
    }

    const pathData = this.#dataSource.getPathData(pathId);
    if (!pathData || pathData.length === 0) {
      return;
    }

    const path = this.#state?.find((p) => p.id === pathId);
    if (!path) {
      return;
    }

    const existingSource = this.#map.getSource(getLineSourceId(pathId));
    const drawing = this.#drawings.get(pathId);
    if (existingSource && existingSource.type === "geojson" && drawing) {
      // Deliberately not redrawn here: the source may still be loading an
      // earlier payload, so the flush is what picks the moment to write. A
      // path already waiting keeps its original deadline, so a stream of
      // small chunks cannot push the deadline out indefinitely.
      drawing.flushDeadline ??= performance.now() + MAX_REDRAW_DELAY_MS;
      this.#scheduleFlush();
      return;
    }

    // No source: either this is the path's first chunk, or a style switch
    // destroyed every source and the path has to be laid down again in full.
    // The fresh drawing record replaces whatever the old incarnation had
    // queued or drawn, all of which is moot.
    const points = this.createPointsGeoJSON(pathData);
    this.#removeHoverInteraction(pathId);
    this.#drawings.set(pathId, {
      lineSamplesConfirmed: 0,
      // The empty collection the line source is created with counts as an
      // outstanding payload covering no samples, so the samples the path
      // already has wait for that load to finish like every later redraw
      // waits for the one before it. The deadline is what marks them as
      // waiting: the source announcing its creation load is what releases
      // them, and a path whose stream has already ended has nothing else
      // coming that would draw it.
      lineSamplesSent: 0,
      flushDeadline: performance.now() + MAX_REDRAW_DELAY_MS,
      pointSelection: points.indices,
      pointsRefreshedAt: null,
      hoverListeners: null,
    });

    // Points live in their own source, holding a selection taken for the
    // viewport, while the line lives in one holding every sample. Keeping
    // them apart is what lets the drawn points stay bounded while the line
    // stays complete.
    this.addSourceAndLayers(
      getPointsSourceId(pathId),
      {
        type: "geojson",
        buffer: CIRCLE_TILE_BUFFER,
        data: points.collection,
      },
      [
        {
          id: getCircleLayerId(pathId),
          type: "circle",
          source: getPointsSourceId(pathId),
          layout: {
            visibility: path.visible ? "visible" : "none",
          },
          paint: {
            "circle-radius": 3,
            "circle-color":
              (path.style["lineColor"] as string) || defaultMarker,
            // Set at creation as well as in applyPathStyle, so a path whose
            // style sets an opacity below 1 when its first chunk arrives is
            // never drawn fully opaque first.
            "circle-opacity": path.style.opacity ?? 1,
          },
        },
      ],
    );

    // The source is created without `dynamic: true`, and so redrawn whole
    // on the schedule above rather than amended with the samples since the
    // last write. Amending is much the cheaper write, but mapbox indexes the
    // two kinds of source differently, and the difference shows up on every
    // pan.
    //
    // Measured on mapbox-gl 3.29.0 with eight paths totalling 960,000
    // coordinates. geojson-vt indexes a source created without that flag and
    // simplifies the geometry for each zoom, so at zoom 11 the tiles held about 2,500
    // coordinates and panning stayed at 60fps. A dynamic source is indexed
    // without any simplification, so the same tiles held all 960,240 and
    // panning ran at about 41fps, over a band from zoom 10 to zoom 12.
    // Panning is what a user does with a map for as long as the panel is
    // open; the write cost is paid once per path, while it loads.
    //
    // Replacing costs more per write and still loads faster overall.
    // Replaying one load's writes, 33 per path with all eight paths running
    // together, the panel reached fully drawn in 1,590ms against 2,359ms of
    // amendments, and its worst single frame was 94ms against 252ms. It
    // spends more main-thread time in total, about 870ms against 205ms, but
    // in pieces small enough that no one of them reads as a stall.
    this.addSourceAndLayers(
      getLineSourceId(pathId),
      {
        type: "geojson",
        // Created holding nothing. The samples the path already has go out
        // on the flush this creation load releases, the way every later
        // sample does.
        data: { type: "FeatureCollection", features: [] },
      },
      [
        {
          id: getLineLayerId(pathId),
          type: "line",
          source: getLineSourceId(pathId),
          layout: {
            "line-join": "round",
            "line-cap": "round",
            visibility: path.visible ? "visible" : "none",
          },
          paint: {
            "line-color": (path.style["lineColor"] as string) || defaultMarker,
            "line-width": 1,
            "line-opacity": path.style.opacity ?? 1,
          },
        },
      ],
    );
    // Mapbox draws layers in the order they were added, so this path's line
    // and points have just landed on top of the markers every path shares.
    // Lifting that layer back up keeps the markers visible.
    if (this.#map.getLayer(ACTIVE_LOCATIONS_ID)) {
      this.#map.moveLayer(ACTIVE_LOCATIONS_ID);
    }

    this.#addHoverInteraction(pathId);
  }

  /**
   * Apply a path's color, opacity, and visibility to the layers already on
   * the map, without rebuilding any GeoJSON. Runs from setState, which is
   * where a recolor, a fade, or a visibility toggle from the legend or
   * panel settings arrives.
   */
  private applyPathStyle(path: MapPath): void {
    if (!this.#map) {
      return;
    }

    const circleLayerId = getCircleLayerId(path.id);
    const lineLayerId = getLineLayerId(path.id);

    // A path whose style sets no color keeps the default it was created
    // with: passing undefined to setPaintProperty would reset the property
    // to mapbox's default, black. A path whose style sets no opacity is
    // drawn fully opaque, which is also mapbox's default for both layers.
    const lineColor = path.style["lineColor"] ?? defaultMarker;
    const opacity = path.style.opacity ?? 1;
    if (this.#map.getLayer(circleLayerId)) {
      this.#map.setPaintProperty(circleLayerId, "circle-color", lineColor);
      this.#map.setPaintProperty(circleLayerId, "circle-opacity", opacity);
    }
    if (this.#map.getLayer(lineLayerId)) {
      this.#map.setPaintProperty(lineLayerId, "line-color", lineColor);
      this.#map.setPaintProperty(lineLayerId, "line-opacity", opacity);
    }

    const visibility = path.visible ? "visible" : "none";
    if (this.#map.getLayer(circleLayerId)) {
      this.#map.setLayoutProperty(circleLayerId, "visibility", visibility);
    }
    if (this.#map.getLayer(lineLayerId)) {
      this.#map.setLayoutProperty(lineLayerId, "visibility", visibility);
    }
    // The path's active-location marker takes no part here: hiding a path
    // drops its marker from the shared source rather than hiding a layer.
  }

  /**
   * Redraw where every visible path is at the current time, as one marker
   * apiece in the source they share. A hidden path contributes no marker, so
   * the layer is never hidden and never needs to be.
   *
   * Called from timer tick/seek/start/stop, from setState, and from
   * StyleToggleControl's onStyleLoad, which is why the source may be absent
   * here: the new style arrives with no sources or layers at all.
   */
  private drawActiveLocation(): void {
    if (!this.#map || !this.#state || !this.#timer) {
      return;
    }

    const locations: { location: TimestampedLocation; pathId: string }[] = [];
    for (const path of this.#state) {
      const pathData = this.#dataSource.getPathData(path.id);
      if (!pathData || pathData.length === 0) {
        continue;
      }

      const newIndex = pathData.findNearestIndex(this.#timer.currentTime);
      const coord = pathData.getCoordinate(newIndex);

      if (!coord) {
        continue;
      }

      const timestampedLocation = {
        latitude: coord.lat,
        longitude: coord.lon,
        timestamp: nanoSecToSecLossy(coord.logTime),
      };

      // A panel showing one path reads that path's current position out
      // alongside the map; with several there is no single position to read.
      if (this.#state.length === 1) {
        this.#setSingleActiveLocation(timestampedLocation);
      }

      if (path.visible) {
        locations.push({ location: timestampedLocation, pathId: path.id });
      }
    }

    const activeLocationsGeoJSON = this.createActiveLocationGeoJSON(locations);
    const activeSource = this.#map.getSource(ACTIVE_LOCATIONS_ID);
    if (activeSource && activeSource.type === "geojson") {
      activeSource.setData(activeLocationsGeoJSON);
      return;
    }

    this.addSourceAndLayers(
      ACTIVE_LOCATIONS_ID,
      {
        type: "geojson",
        buffer: CIRCLE_TILE_BUFFER,
        data: activeLocationsGeoJSON,
      },
      [
        {
          id: ACTIVE_LOCATIONS_ID,
          type: "circle",
          source: ACTIVE_LOCATIONS_ID,
          paint: activeLayerStyle,
        },
      ],
    );
  }

  /**
   * Fit the camera to the merged bounds of every visible path, so the whole
   * panel's data is in view. Runs after each streamed data chunk, unless the
   * panel opened with a restored viewport or the user has taken the camera
   * over since (a camera gesture, or picking a path in the legend).
   *
   * Returns whether the camera was actually asked to move, so the caller
   * knows a moveend is coming that will re-take the point selections.
   */
  private fitCameraToVisiblePathBounds(): boolean {
    if (!this.#map || !this.#shouldFitCameraToPathBounds) {
      return false;
    }

    const cameraFit = cameraFitForVisiblePaths(this.#state ?? [], (pathId) =>
      this.#dataSource.getPathData(pathId)?.getBounds(),
    );
    if (!cameraFit) {
      return false;
    }

    // A fit that would land on the same corners still re-renders the viewport,
    // so it is worth not asking for one.
    if (isEqual(this.#lastFittedCorners, cameraFit.corners)) {
      return false;
    }

    this.#lastFittedCorners = cameraFit.corners;
    this.#refreshPointsWhenCameraSettles = true;
    this.#map.fitBounds(cameraFit.corners, cameraFit.options);
    return true;
  }

  /**
   * A chunk of a path's samples arrived. The chunk is queued rather than drawn
   * from here, so several chunks landing before the next frame cost one write.
   */
  #onPathDataUpdated = (pathId: string): void => {
    this.drawPathData(pathId);
    // Deliberately not fitted here: see #cameraFitPending.
    this.#cameraFitPending = true;
    this.#scheduleFlush();
  };

  /**
   * Callback fired when a path's data has fully loaded. The end of a load
   * has no later chunk behind it, so the two mid-load economies are released
   * here: samples held back because the path had grown too little to be worth
   * a redraw are queued to go out on the next frame, and the drawn points are
   * re-taken immediately (a complete path is exempt from the refresh
   * throttle), so the line and the points cannot end the load disagreeing
   * about the tail.
   */
  #onPathDataComplete = (pathId: string): void => {
    this.drawPathData(pathId);
    this.#refreshPathPoints(pathId);
  };

  /**
   * Whether a failure the map reported came from a source other than the ones
   * this renderer laid down.
   *
   * Mapbox stamps every event a source fires with that source's id, and
   * stamps none on a style-level failure, so an id that is none of ours names
   * the map underneath the paths. That map failing is not the panel's
   * failure: an access token without permission to fetch tiles, for one,
   * fails every tile from the first frame, and the paths, the markers, the
   * camera, and clicking and hovering all still work over blank ground.
   * Mapbox asks for those tiles again on the next camera move, so it can also
   * come back on its own.
   */
  #isBasemapSourceError(event: mapboxgl.ErrorEvent): boolean {
    const sourceId = erroringSourceId(event);
    if (sourceId === undefined || sourceId === ACTIVE_LOCATIONS_ID) {
      return false;
    }

    for (const pathId of this.#drawings.keys()) {
      if (
        sourceId === getLineSourceId(pathId) ||
        sourceId === getPointsSourceId(pathId)
      ) {
        return false;
      }
    }
    return true;
  }

  /**
   * A runtime failure the map reported. mapbox fires these rather than
   * throwing, for a GeoJSON payload its worker rejected among others, so
   * this is the only channel that surfaces them.
   *
   * When the erroring source is one of ours, that path's line payload never
   * landed, so the map keeps showing what the payload before it drew. The
   * path is queued to be drawn again on the usual redraw deadline. Every
   * other path's payload is still in flight and untouched.
   */
  #onMapError(event: mapboxgl.ErrorEvent): void {
    // mapbox promises only a message on this payload, so anything that did
    // not arrive as an Error is re-wrapped to keep the reporting channel
    // typed on Error.
    this.#onError(
      event.error instanceof Error
        ? event.error
        : new Error(event.error.message),
    );

    const sourceId = erroringSourceId(event);
    if (sourceId === undefined) {
      return;
    }

    // A line source is named for its path, so the drawings are keyed by the
    // same string; a points source's id ends in a suffix and matches none of
    // them, which is right, because a points payload is replaced wholesale
    // by the next refresh rather than re-sent.
    const drawing = this.#drawings.get(sourceId);
    if (!drawing || drawing.lineSamplesSent === null) {
      return;
    }

    drawing.lineSamplesSent = null;
    drawing.flushDeadline ??= performance.now() + MAX_REDRAW_DELAY_MS;
    this.#scheduleFlush();
  }

  /**
   * A source finished loading a payload. For a path's line source this
   * confirms the payload most recently sent, advancing how much of the path
   * the map is showing, and resumes the path if samples were held back while
   * that payload was outstanding.
   */
  #onSourceData(event: mapboxgl.MapSourceDataEvent): void {
    // Tile loads fire this event too, with no sourceDataType; only
    // "metadata" (a source's first load) and "content" (each one after) mark
    // a payload load finishing.
    if (
      event.sourceDataType !== "metadata" &&
      event.sourceDataType !== "content"
    ) {
      return;
    }

    // Events the map raises about itself rather than about one source carry
    // no source id, and name no path to advance.
    if (event.sourceId === undefined) {
      return;
    }

    const drawing = this.#drawings.get(event.sourceId);
    if (!drawing) {
      return;
    }

    if (drawing.lineSamplesSent !== null) {
      drawing.lineSamplesConfirmed = drawing.lineSamplesSent;
      drawing.lineSamplesSent = null;
    }

    if (drawing.flushDeadline !== null) {
      this.#scheduleFlush();
    }
  }

  /**
   * Time to seek to for a click that landed on a drawn point. `drawn` is
   * the path the clicked feature was drawn for (`#drawingForFeature`).
   *
   * The drawn points are a bounded selection, so away from full zoom the one
   * under the cursor stands in for the samples either side of it that were not
   * drawn. Those samples are still in memory, so the click is resolved against
   * them rather than snapped to the stand-in. At full resolution the seek is
   * exact; away from it, it lands on the nearest sample within the span the
   * clicked point stands for, which for a track that crosses itself can
   * differ from the nearest sample on screen.
   */
  #timestampForClick(
    feature: mapboxgl.GeoJSONFeature,
    clickedAt: mapboxgl.LngLat,
    drawn: { pathId: string; drawing: PathDrawing } | undefined,
  ): number | undefined {
    const drawnTimestamp = feature.properties?.timestamp as number | undefined;

    // A feature from a source no drawing owns, the active-location marker
    // for instance, carries its timestamp in its own properties.
    if (!drawn) {
      return drawnTimestamp;
    }
    const { pathId, drawing } = drawn;

    const drawnIndex: unknown = feature.properties?.id;
    if (typeof drawnIndex !== "number") {
      return drawnTimestamp;
    }

    const pathData = this.#dataSource.getPathData(pathId);
    if (!pathData) {
      return drawnTimestamp;
    }

    const drawnAt = drawing.pointSelection.indexOf(drawnIndex);
    if (drawnAt === -1) {
      return drawnTimestamp;
    }

    // Resolve the click against the samples this drawn point stands for:
    // those between its neighboring drawn points, reaching one point spacing
    // past an end of the selection. Everything farther is off screen, so a
    // click on the selection's edge never scans the path's off-screen
    // remainder.
    const { fromIndex, toIndexExclusive } = representedSampleRange(
      drawing.pointSelection,
      drawnAt,
    );
    const nearest = pathData.nearestIndexTo(
      clickedAt.lat,
      clickedAt.lng,
      fromIndex,
      toIndexExclusive,
    );
    if (nearest === undefined) {
      return drawnTimestamp;
    }

    const coord = pathData.getCoordinate(nearest);
    return coord ? nanoSecToSecLossy(coord.logTime) : drawnTimestamp;
  }

  /**
   * The path a rendered feature was drawn for, as its id and drawing record.
   * The feature names only the source it came from, so the path is the one
   * whose points source that is; a feature of any other source (the
   * active-location marker) belongs to no drawing.
   */
  #drawingForFeature(
    feature: mapboxgl.GeoJSONFeature,
  ): { pathId: string; drawing: PathDrawing } | undefined {
    for (const [pathId, drawing] of this.#drawings) {
      if (getPointsSourceId(pathId) === feature.source) {
        return { pathId, drawing };
      }
    }
    return undefined;
  }

  /**
   * The layers a click can resolve to: each drawn path's sample circles, and
   * the markers every path's current location shares. Naming them is what
   * keeps a click from hit-testing the basemap's roads, labels and buildings
   * on its way to a feature that can only have come from one of these.
   *
   * Layers the style does not currently hold are left out. mapbox answers an
   * unknown layer id with an error and an empty result, which would take the
   * whole query with it, and a path's layers really are absent for a moment
   * after a style switch destroys them.
   */
  #clickableLayerIds(): string[] {
    const candidates = [...this.#drawings.keys()].map(getCircleLayerId);
    candidates.push(ACTIVE_LOCATIONS_ID);
    return candidates.filter((layerId) => this.#map?.getLayer(layerId));
  }

  /**
   * Tell the host which path the user clicked, and seek playback to the
   * sample the click landed on. A click that lands on none of the drawn
   * points or markers does neither; a click on an active-location marker
   * seeks without naming a path, since the marker belongs to no drawing.
   */
  private onMapClick(event: mapboxgl.MapMouseEvent): void {
    if (!this.#map) {
      return;
    }

    const features = this.#map.queryRenderedFeatures(event.point, {
      layers: this.#clickableLayerIds(),
    });

    if (features.length) {
      const clickedFeature = features.find(
        (feature) =>
          feature.geometry.type === "Point" &&
          feature.properties?.timestamp !== undefined,
      );

      if (clickedFeature && clickedFeature.properties) {
        const drawn = this.#drawingForFeature(clickedFeature);
        const timestamp = this.#timestampForClick(
          clickedFeature,
          event.lngLat,
          drawn,
        );

        if (timestamp !== undefined && !isNaN(timestamp)) {
          if (drawn !== undefined) {
            this.#onPathClick?.({ pathId: drawn.pathId });
          }
          this.#timer?.seekTo(BigInt(Math.round(timestamp * NS_PER_S)));
        }
      }
    }
  }

  /**
   * Drop the paths the new state no longer lists: take their map objects off,
   * and release the samples the data source is holding for them.
   */
  private removeOldPaths(state: MapPath[]): void {
    const pathsToRemove = (this.#state ?? []).filter((existingPaths) => {
      const matching = state.find((path) => path.id === existingPaths.id);
      return matching === undefined;
    });

    for (const path of pathsToRemove) {
      this.#teardownPath(path.id);
      this.#dataSource.disposePath(path);
    }
  }

  /**
   * Show the sample under the pointer in a popup while it is over one path's
   * drawn points, and hide it again when the pointer leaves them.
   *
   * The handlers are kept on the path's drawing record because mapbox holds a
   * listener until it is handed the same function back. A style switch
   * destroys every layer and the renderer lays them all down again, so
   * without a matching removal each switch would leave another pair attached
   * to the path and every later pointer move would run all of them.
   */
  #addHoverInteraction(pathId: string): void {
    const drawing = this.#drawings.get(pathId);
    if (!this.#map || !drawing) {
      return;
    }

    const layerId = getCircleLayerId(pathId);

    const mousemove: HoverListener = (e) => {
      if (!this.#map) {
        return;
      }

      this.#map.getCanvas().style.cursor = "pointer";

      if (e.features && e.features.length) {
        const hoveredFeature = e.features.find(
          (feature) =>
            feature.geometry.type === "Point" &&
            feature.properties?.timestamp !== undefined,
        );

        if (hoveredFeature && hoveredFeature.properties) {
          const coordinates = e.lngLat;
          const timestamp = hoveredFeature.properties.timestamp as number;

          if (isNaN(timestamp)) {
            return;
          }

          this.#popup
            ?.setLngLat(coordinates)
            .setHTML(
              `
            <div style="padding-top: 2px;color: #333333;">
              <div>${coordinates.lat.toFixed(6)}, ${coordinates.lng.toFixed(6)}</div>
              <div>${nanoSecToLocalTimestamp(BigInt(Math.round(timestamp * NS_PER_S)))}</div>
            </div>
          `,
            )
            .addTo(this.#map);
        }
      }
    };

    const mouseleave: HoverListener = () => {
      if (!this.#map) {
        return;
      }
      this.#map.getCanvas().style.cursor = "";
      this.#popup?.remove();
    };

    this.#map.on("mousemove", layerId, mousemove);
    this.#map.on("mouseleave", layerId, mouseleave);
    drawing.hoverListeners = { mousemove, mouseleave };
  }

  /**
   * Take a path's popup handlers back off the map. Does nothing for a path
   * that has none, which is every path before its layers are first added and
   * every path this has already run for.
   */
  #removeHoverInteraction(pathId: string): void {
    const drawing = this.#drawings.get(pathId);
    if (!this.#map || !drawing?.hoverListeners) {
      return;
    }

    const layerId = getCircleLayerId(pathId);
    this.#map.off("mousemove", layerId, drawing.hoverListeners.mousemove);
    this.#map.off("mouseleave", layerId, drawing.hoverListeners.mouseleave);
    drawing.hoverListeners = null;
  }
}

// TODO:
// De-dupe issue Gabe mentioned on PR
// Determine strategy for handling active point in multi-series case
